<template>
  <div class="container" ref="mapDiv">
  </div>
</template>

<script>
import JSZip from 'jszip';
import { loadModules } from 'esri-loader';
import moment from 'moment';

export default {
  props: {
    projectData: {
      type: Object,
      default: () => ({}),
    },
  },
  name: 'MapComponentTRA',
  data() {
    return {
      map: null,
      view: null,
      featureLayer: null,
      resData: null,
      PictureMarkerSymbol: null,
      PopupTemplate: null,
      Graphic: null,
      locations: [],
      markerPics: {
        'GREEN': require('@/assets/imgur/MHUKBDR.png'),
        'AL': require('@/assets/imgur/hs3jCEL.png'),
        'WSL': require('@/assets/imgur/J3geGVe.png'),
        'down': require('@/assets/imgur/ZeDDvon.png'),
        'WL': require('@/assets/imgur/s5Jqg7J.png'),
        'default': require('@/assets/imgur/MHUKBDR.png'),
      },
    };
  },
  computed: {
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
  },
  async mounted() {
    this.resData = this.projectData;

    const option = {
      url: 'https://js.arcgis.com/4.28/init.js',
      css: 'https://js.arcgis.com/4.28/esri/themes/light/main.css',
    };

    // ESRI 模塊
    const [Map, SceneView, Graphic, FeatureLayer, PictureMarkerSymbol, PopupTemplate, KMLLayer, reactiveUtils] = await loadModules([
      'esri/Map',
      'esri/views/SceneView', // 改為 SceneView
      'esri/Graphic',
      'esri/layers/FeatureLayer',
      'esri/symbols/PictureMarkerSymbol',
      'esri/PopupTemplate',
      'esri/layers/KMLLayer',
      'esri/core/reactiveUtils',
    ], option);

    this.Graphic = Graphic;
    this.PictureMarkerSymbol = PictureMarkerSymbol;
    this.PopupTemplate = PopupTemplate;
    this.FeatureLayer = FeatureLayer;
    this.reactiveUtils = reactiveUtils;

    // 創建 3D 地圖（加入地形起伏）
    this.map = new Map({
      basemap: 'topo-vector',
      ground: 'world-elevation', // 加入 3D 全球高程
      layers: [],
    });

    // 創建 3D 場景視圖
    this.view = new SceneView({
      container: this.$refs.mapDiv,
      map: this.map,
      camera: {
        position: {
          longitude: 121.25,
          latitude: 24.15,
          z: 75000,    // 視角高度（公尺）
        },
        tilt: 50,      // 俯仰傾斜角度 (0 為正上方俯視，值越大越平視)
        heading: 350,  // 方位角 (朝北/東北)
      },
      constraints: {
        tilt: { max: 70 }, // 限制最大傾角，避免視角過於貼地難以操作
      },
    });

    const kmlList = [
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-宜蘭線-路線座標(K000+000~K94+000) -北迴線-路線座標(K000+000~K79+400)(ORI).kml',
        color: '#2b7bff', // 藍色
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-平溪線及深澳線(ORI).kml',
        color: '#E75000', // 橘紅色
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/北迴線.kml',
        color: '#66CC00', // 
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/宜蘭線.kml',
        color: '#D4C21D', // 
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-深澳線.kml',
        color: '#FF4FA8', // 
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-平溪線.kml',
        color: '#8C3BFF', // 
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/北工段百公尺標(八堵)_1.kmz',
        color: '#409EFF', // 
      },
      {
        url: 'https://api-geoserver.geosupply.com.tw/kmz/北工段百公尺標線.kml',
        color: '#008B8B', // 
      },
    ];
    
    for (const item of kmlList) {
      try {
        const graphics = await this.loadKmlGeometries(item.url);
        
        if (graphics && graphics.length > 0) {
          const layer = this.createKmlFeatureLayer(graphics, item.color);
          this.map.add(layer);
        }
      } catch (error) {
        console.error(`無法解析檔案: ${item.url}`, error);
      }
    }

    this.view.ui.remove('zoom');

    this.reactiveUtils.on(
      () => this.view.popup?.viewModel,
      'trigger-action',
      (event) => {
        if (event?.action?.id === 'show-instrument-id') {
          const feat = this.view.popup.selectedFeature;
          const instrumentId = feat?.attributes?.instrument_id;
          if (instrumentId) {
            window.open(`/chart?instrument=${instrumentId}`, '_blank');
          } else {
            console.warn('No instrument_id on selectedFeature');
          }
        }
      },
    );

    this.getProject();
  },
  methods: {
    async loadKmlGeometries(url) {
      let kmlText = '';

      // 1. 下載檔案 (轉成 Blob)
      const response = await fetch(url);
      const blob = await response.blob();

      // 2. 判斷是 KMZ 還是 KML
      if (url.toLowerCase().endsWith('.kmz')) {
        // 使用 JSZip 解壓縮
        const zip = await JSZip.loadAsync(blob);
        
        // 尋找裡面的 .kml 檔案 (通常叫 doc.kml，但也可能叫別的)
        const kmlFileName = Object.keys(zip.files).find(name => name.endsWith('.kml'));
        
        if (kmlFileName) {
          kmlText = await zip.file(kmlFileName).async('string');
        } else {
          throw new Error('KMZ 檔案中找不到 .kml 檔');
        }
      } else {
        // 如果是原本的 KML，直接轉文字
        kmlText = await blob.text();
      }

      // 3. 解析 XML (維持你原本的邏輯)
      const parser = new DOMParser();
      const xml = parser.parseFromString(kmlText, 'application/xml');

      const placemarks = xml.getElementsByTagName('Placemark');
      const graphics = [];
      let oid = 1;

      for (let p of placemarks) {
        const nameNode = p.getElementsByTagName('name')[0];
        const name = nameNode?.textContent || 'NoName';

        // ... (以下維持你原本的解析邏輯: LineString, Point) ...
        // ======== 1) LineString ========
        const lineNode = p.getElementsByTagName('LineString')[0];
        if (lineNode) {
          const coordNode = lineNode.getElementsByTagName('coordinates')[0];
          if (!coordNode) continue;
          const coordsText = coordNode.textContent.trim();
          const paths = coordsText.split(/\s+/).map(c => c.split(',').map(Number).slice(0, 2));
          
          graphics.push(new this.Graphic({
            geometry: { type: 'polyline', paths: [paths] },
            attributes: { ObjectID: oid++, name },
          }));
          continue;
        }

        // ======== 2) Point ========
        const pointNode = p.getElementsByTagName('Point')[0];
        if (pointNode) {
          const coordNode = pointNode.getElementsByTagName('coordinates')[0];
          if (!coordNode) continue;
          const [lon, lat] = coordNode.textContent.trim().split(',').map(Number);
          
          graphics.push(new this.Graphic({
            geometry: { type: 'point', longitude: lon, latitude: lat },
            attributes: { ObjectID: oid++, name },
          }));
          continue;
        }
      }

      return graphics;
    },
    createKmlFeatureLayer(graphics, colorHex) {
      return new this.FeatureLayer({
        source: graphics,
        objectIdField: 'ObjectID',

        elevationInfo: {
          mode: 'on-the-ground',
        },
        
        fields: [
          { name: 'ObjectID', type: 'oid' },
          { name: 'name', type: 'string' },
        ],

        renderer: {
          type: 'simple',
          symbol: {
            type: graphics[0]?.geometry.type === 'polyline'
              ? 'simple-line'
              : 'simple-marker',

            color: colorHex,
            width: 2,
            size: 6,
            style: 'solid',
            outline: { color: 'white', width: 1 },
          },
        },

        labelingInfo: [
          {
            labelExpressionInfo: { expression: '$feature.name' },
            symbol: {
              type: 'text',
              color: 'black',
              haloColor: 'white',
              haloSize: 1,
              font: { size: 10 },
            },
            labelPlacement: 'center-right',
            deconflictionStrategy: 'dynamic',
          },
        ],
      });
    },
    getStatus(area) {
      this.locations.forEach(t => {
        if (!area.name.includes(t.id)) return;

        let hasWSL = false;
        let hasAL = false;
        let totalCount = 0;
        let downCount = 0;

        (area.station || []).forEach(st => {
          (st.instrument || []).forEach(item => {
            totalCount++;
            if (item.status === 'WSL') hasWSL = true;
            else if (item.status === 'AL') hasAL = true;
            else if (item.status === 'down') downCount++;
          });
        });

        if (hasWSL) {
          t.status = 'WSL';
        } else if (hasAL) {
          t.status = 'AL';
        } else if (totalCount > 0 && downCount === totalCount) {
          t.status = 'down';
        }
      });
    },
    addMarker(name, lon, lat, status, useBlueDot = false, reading = 'NAN', timestamp = 'NAN', uid = 'NAN') {

      let point = {
        type: 'point',
        longitude: lon,
        latitude: lat,
      };

      let markerSymbol;

      if (useBlueDot) {
        markerSymbol = {
          type: 'simple-marker',
          style: 'circle',
          color: '#2b7bff',
          size: 6,
          outline: { color: 'white', width: 1 },
        };
      } else {
        const url = this.markerPics[status] || this.markerPics.default;
        markerSymbol = new this.PictureMarkerSymbol({ url, width: '12px', height: '12px' });
      }

      const pointGraphic = new this.Graphic({
        geometry: point,
        symbol: markerSymbol,
        popupTemplate: new this.PopupTemplate({
          title: name,
          content: '',//`<p style="padding-top: 10px;">Last: ${reading}</p><p>${moment(timestamp).format('YYYY-MM-DD HH:mm')}</p>`,
          actions: [{
            id: 'show-instrument-id',
            title: 'chart',
            className: 'esri-icon-chart',
          }],
        }),
        attributes: {
          instrument_id: uid,
          status,
        },
      });

      this.view.graphics.add(pointGraphic);

      const textGraphic = new this.Graphic({
        geometry: point,
        symbol: {
          type: 'text',
          text: name,
          color: 'black',
          font: {
            size: 10,
            weight: 'bold',
          },
          haloColor: 'white',
          haloSize: 2,
          yoffset: -12,
        },
      });

      if (!uid)
        this.view.graphics.add(textGraphic);

      if (!this.labelGraphics) this.labelGraphics = [];
      this.labelGraphics.push(textGraphic);
    },
    getProject() {
      const allInstruments = [];

      // 1. 扁平化收集所有具備座標的儀器
      (this.resData.area || []).forEach(area => {
        this.getStatus(area);
        (area.station || []).forEach(st => {
          (st.instrument || []).forEach(item => {
            const coords = item.latestVersion?.coordinates;
            if (coords?.[0] && coords?.[1]) {
              allInstruments.push(item);
            }
          });
        });
      });

      // 2. 設定圖層渲染權重（數字越小越早 addMarker，疊在越底層）
      const statusPriority = {
        'down': 1,
        'default': 6,
        'GREEN': 6,
        'WL': 3,
        'AL': 4,
        'WSL': 5,
      };

      // 3. 進行排序：優先級低 (down) 的排前面
      allInstruments.sort((a, b) => {
        const priorityA = statusPriority[a.status] || 2;
        const priorityB = statusPriority[b.status] || 2;
        return priorityA - priorityB;
      });

      // 4. 依排序後的順序建立 Marker
      allInstruments.forEach(item => {
        const coords = item.latestVersion.coordinates;
        const lon = Number(coords[0]);
        const lat = Number(coords[1]);
        this.addMarker(
          item.latestVersion.name,
          lon,
          lat,
          item.status,
          false,
          item.latestVersion.reading,
          item.latestVersion.timestamp,
          item._id,
        );
      });
    },
  },
};
</script>

<style scoped>
::v-deep [class*="esri-popup--is-docked-top-"] .esri-popup__footer,
[class*="esri-popup--aligned-bottom-"] .esri-popup__footer {
  border-bottom: solid 1px #6e6e6e4d;
}

::v-deep .esri-popup__header {
  border-top-left-radius: 5px !important;
  border-top-right-radius: 5px;
  background-color: #fff;
  color: #fff;
}

::v-deep .esri-popup--shadow {
  box-shadow: 0 1px 4px rgb(155, 155, 155) !important;
}

::v-deep .esri-popup__header-title {
  background-color: #409EFF !important;
}

::v-deep .esri-popup__header-container,
.esri-popup__header-container--button {
  outline: none !important;
}

::v-deep .esri-popup__icon,
.esri-icon-close {
  color: #000000 !important;
}

::v-deep .esri-view-width-xlarge .esri-popup__main-container {
  width: 160px !important;
  border-radius: 5px !important;
}

::v-deep .esri-ui .esri-popup {
  width: 160px !important;
  border-radius: 5px !important;
}

::v-deep .esri-popup__button {
  background-color: transparent !important;
  outline: none;
  display: none;
}

::v-deep .esri-popup__header-title {
  font-weight: 600 !important;
}

::v-deep .esri-view .esri-view-surface--inset-outline:focus::after {
  outline: auto 0px Highlight !important;
  outline: auto 0px -webkit-focus-ring-color !important;
}
</style>

<style scoped>
.container {
  width: 100%;
  height: 100vh;
  max-height: 555px;
}
</style>

<style>
.center-item {
  display: flex;
  align-items: center;
  padding-left: 2px;
}

.icon-text {
  padding-left: 5px;
  padding-right: 2px;
}

.esri-view .esri-view-surface--touch-none:focus::after {
  outline: none !important;
}
</style>